import React, { FC, useState } from 'react';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';
import { Asset, InfoFilledIcon, Tooltip } from '@clearcover/atlas';
import classnames from 'classnames';

import { Column, FormInput, Row } from 'Shared/components';
import { formatPhoneNumber } from 'Shared/utils';
import {
  formatAddressDisplay,
  getContactInfoByFieldValue,
} from 'QuoteApp/utils';
import { RoutePrompt } from 'QuoteApp/components';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import {
  sanitizeNumString,
  sanitizeString,
} from 'QuoteApp/services/endpoints/utils';

import { emailValidator } from './contactInfoValidation';
import { InlineEditRow } from './InlineEditRow';
import styles from './styles.module.css';

type ContactInfoV2Type = {
  setIsEditing: (isEditing: boolean) => void;
};

const PHONE_TYPE_OPTIONS = { mobile: 'Mobile', home: 'Home' };

export const ContactInfoV2: FC<ContactInfoV2Type> = ({ setIsEditing }) => {
  const { details, api, fetchRate, refreshDetails } = useAppContext();
  const { getSignaturePacket } = usePurchaseContext();

  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [phoneType, setPhoneType] = useState(
    details.contactInfo[details.principalInsuredId].phoneType.value ?? ''
  );

  const contactInfoSchema = Yup.object().shape({
    contactInfo: Yup.object().shape({
      [details.principalInsuredId]: Yup.object().shape({
        email: emailValidator,
        phoneNumber: Yup.string()
          .transform((value: string) => value.replace(/[^0-9.]/g, ''))
          .min(10, 'Must be 10 numeric digits')
          .required('Phone Number is required'),
      }),
    }),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={{
        contactInfo: getContactInfoByFieldValue(details.contactInfo, 'value'),
      }}
      validationSchema={contactInfoSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await api.saveAppData({
            data: {
              insurableParties: [
                {
                  identifier: details.principalInsuredId,
                  contactInformation: {
                    email: sanitizeString(
                      values.contactInfo[details.principalInsuredId].email
                    ),
                    telephone: {
                      phoneType: sanitizeString(phoneType),
                      number: sanitizeNumString(
                        values.contactInfo[details.principalInsuredId]
                          .phoneNumber
                      ),
                    },
                  },
                },
              ],
            },
          });

          await fetchRate();
          refreshDetails();
          getSignaturePacket();

          setIsEditing(false);
          setIsEditingPhoneNumber(false);
          setIsEditingEmail(false);
        } catch {
          // api.saveAppData will throw a toast error
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        touched,
        errors,
        dirty,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
        resetForm,
      }) => {
        const resetFields = () => {
          resetForm();
          setIsEditingEmail(false);
          setIsEditingPhoneNumber(false);
          setIsEditing(false);
          setPhoneType(
            details.contactInfo[details.principalInsuredId].phoneType.value ??
              ''
          );
        };

        return (
          <>
            <RoutePrompt
              when={dirty && !isSubmitting}
              editingIdDisplay=""
              isValid={isValid}
            />
            <h3 className={styles.contactInfoHeading}>
              Confirm contact information for communication and documents
            </h3>
            <div className={styles.contactInfoForm2}>
              <InlineEditRow
                label="Phone Number"
                displayValue={
                  <span
                    className={classnames('fs-mask', styles.contactInfoValue)}
                  >
                    {formatPhoneNumber(
                      details.contactInfo[details.principalInsuredId]
                        .phoneNumber.value || ''
                    )}
                    {phoneType &&
                      ` · ${
                        PHONE_TYPE_OPTIONS[
                          phoneType as keyof typeof PHONE_TYPE_OPTIONS
                        ] ?? phoneType
                      }`}
                  </span>
                }
                isEditing={isEditingPhoneNumber}
                isSubmitting={isSubmitting}
                isValid={isValid}
                onEdit={() => {
                  resetFields();
                  setIsEditingPhoneNumber(true);
                  setIsEditing(true);
                }}
                onSave={handleSubmit}
                onCancel={resetFields}
                editButtonTestId="phone-number-edit-button"
                cancelButtonTestId="cancel-edit-phone-number"
                editContent={
                  <div className={styles.phoneEditRow}>
                    <FormInput
                      data-testid="phone-number"
                      variant="text"
                      name={`contactInfo.${details.principalInsuredId}.phoneNumber`}
                      value={formatPhoneNumber(
                        values.contactInfo[details.principalInsuredId]
                          .phoneNumber
                      )}
                      className={styles.contactInfoEditInput}
                      label="Phone Number"
                      disabled={isSubmitting}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      maxLength={12}
                      errorMessage={
                        getIn(
                          touched,
                          `contactInfo.${details.principalInsuredId}.phoneNumber`
                        )
                          ? getIn(
                              errors,
                              `contactInfo.${details.principalInsuredId}.phoneNumber`
                            )
                          : undefined
                      }
                    />
                    <FormInput
                      data-testid="phone-type"
                      variant="select"
                      name="phoneType"
                      label="Type"
                      value={phoneType}
                      options={PHONE_TYPE_OPTIONS}
                      disabled={isSubmitting}
                      onChange={(value: string) => setPhoneType(value)}
                      className={styles.phoneTypeSelect}
                    />
                  </div>
                }
              />

              <InlineEditRow
                label="Email Address"
                displayValue={
                  <span
                    className={classnames('fs-mask', styles.contactInfoValue)}
                  >
                    {details.contactInfo[details.principalInsuredId].email
                      .value || ''}
                  </span>
                }
                isEditing={isEditingEmail}
                isSubmitting={isSubmitting}
                isValid={isValid}
                onEdit={() => {
                  resetFields();
                  setIsEditingEmail(true);
                  setIsEditing(true);
                }}
                onSave={handleSubmit}
                onCancel={resetFields}
                editButtonTestId="email-edit-button"
                cancelButtonTestId="cancel-edit-email-address"
                editContent={
                  <FormInput
                    data-testid="email-address"
                    variant="text"
                    name={`contactInfo.${details.principalInsuredId}.email`}
                    value={values.contactInfo[details.principalInsuredId].email}
                    label="Email Address"
                    disabled={isSubmitting}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={
                      getIn(
                        touched,
                        `contactInfo.${details.principalInsuredId}.email`
                      )
                        ? getIn(
                            errors,
                            `contactInfo.${details.principalInsuredId}.email`
                          )
                        : undefined
                    }
                  />
                }
              />

              <Row className={styles.contactInfoRow}>
                <Column className={styles.contactInfoFormColumn}>
                  <div className={styles.contactInfoLabel}>Primary Address</div>
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  <span
                    className={classnames('fs-mask', styles.contactInfoValue)}
                  >
                    {formatAddressDisplay(
                      details.address[details.principalInsuredId]
                    )}
                  </span>
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  <Tooltip
                    tooltip={
                      <div className={styles.primaryAddressInfoTooltip}>
                        Please go to the Summary screen to update the
                        policyholder address.
                      </div>
                    }
                    position="top"
                  >
                    <Asset
                      icon={InfoFilledIcon}
                      height={18}
                      width={18}
                      description="Primary Address Information"
                      className={styles.primaryAddressInfoIcon}
                    />
                  </Tooltip>
                </Column>
              </Row>
            </div>
          </>
        );
      }}
    </Formik>
  );
};
